// Operates on plain objects (not @sentry/nextjs or @sentry/react types) so
// it can be shared by both SDKs' beforeSend hooks unmodified.

const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const ILLUMINATED_DOMAINS = new Set(["dioptra.ai", "icertis.com"]);

export function _maskEmail(email: string): string {
  const lowerCasedEmail = email.toLowerCase();
  const [local, domain] = lowerCasedEmail.split("@");
  if (ILLUMINATED_DOMAINS.has(domain)) return lowerCasedEmail;

  if (local.length <= 1) return `*@${domain}`;
  if (local.length <= 2) return `${local[0]}*@${domain}`;

  const maskedLocal = `${local[0]}${"*".repeat(local.length - 2)}${local[local.length - 1]}`;
  return `${maskedLocal}@${domain}`;
}

export function maskEmailsInString(value: string): string {
  return value.replace(EMAIL_REGEX, (match) => _maskEmail(match));
}

const MAX_SCRUB_DEPTH = 8;

function scrubValue(value: unknown, depth = 0): unknown {
  if (typeof value === "string") {
    return maskEmailsInString(value);
  }

  if (depth > MAX_SCRUB_DEPTH) return value;

  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1));
  }

  if (value && typeof value === "object") {
    const scrubbed: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      scrubbed[k] = scrubValue(v, depth + 1);
    }
    return scrubbed;
  }

  return value;
}

// Record<string, unknown> rather than a concrete Sentry type, so this stays SDK-agnostic.
type SentryEvent = Record<string, unknown>;

export function scrubEvent(event: SentryEvent): SentryEvent {
  if (event.user) {
    event.user = scrubUser(event.user as Record<string, unknown>);
  }
  if (Array.isArray(event.breadcrumbs) && event.breadcrumbs.length) {
    event.breadcrumbs = scrubBreadcrumbs(event.breadcrumbs);
  }
  scrubExtraContexts(event);
  if (event.exception) {
    event.exception = scrubException(event.exception as Record<string, unknown>);
  }
  return event;
}

export function scrubUser(user: Record<string, unknown>): Record<string, unknown> {
  const scrubbed = { ...user };
  if (typeof scrubbed.email === "string") {
    scrubbed.email = maskEmailsInString(scrubbed.email);
  }
  if (typeof scrubbed.username === "string") {
    scrubbed.username = maskEmailsInString(scrubbed.username);
  }
  return scrubbed;
}

export function scrubBreadcrumbs(breadcrumbs: unknown[]): unknown[] {
  return breadcrumbs.map((crumb) => {
    if (!crumb || typeof crumb !== "object") return crumb;
    const cleaned = { ...crumb } as Record<string, unknown>;
    if (typeof cleaned.message === "string") {
      cleaned.message = maskEmailsInString(cleaned.message);
    }
    if (cleaned.data && typeof cleaned.data === "object") {
      cleaned.data = scrubValue(cleaned.data);
    }
    return cleaned;
  });
}

export function scrubException(exception: Record<string, unknown>): Record<string, unknown> {
  const values = exception.values;
  if (!Array.isArray(values) || values.length === 0) return exception;

  return {
    ...exception,
    values: values.map((ex) => {
      if (!ex || typeof ex !== "object") return ex;
      const cleaned = { ...ex } as Record<string, unknown>;
      if (typeof cleaned.value === "string") {
        cleaned.value = maskEmailsInString(cleaned.value);
      }
      if (typeof cleaned.type === "string") {
        cleaned.type = maskEmailsInString(cleaned.type);
      }
      const stacktrace = cleaned.stacktrace;
      if (stacktrace && typeof stacktrace === "object" && !Array.isArray(stacktrace)) {
        const st = stacktrace as Record<string, unknown>;
        if (Array.isArray(st.frames) && st.frames.length) {
          cleaned.stacktrace = {
            ...st,
            frames: st.frames.map((frame) => scrubValue(frame)),
          };
        }
      }
      return cleaned;
    }),
  };
}

export function scrubExtraContexts(event: SentryEvent): void {
  if (event.extra) {
    event.extra = scrubValue(event.extra) as Record<string, unknown>;
  }
  if (event.contexts) {
    event.contexts = scrubValue(event.contexts) as Record<string, unknown>;
  }
  if (typeof event.message === "string") {
    event.message = maskEmailsInString(event.message);
  }
  if (event.logentry) {
    event.logentry = typeof event.logentry === "string"
      ? maskEmailsInString(event.logentry)
      : scrubValue(event.logentry) as Record<string, unknown>;
  }
}

// Spread into each app's replayIntegration() call; override only what that app needs.
export const REPLAY_PRIVACY_DEFAULTS = {
  maskAllText: false,
  blockAllMedia: true,
} as const;
